import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import { installFetchGuardrail } from './lib/compliance.js';
import { installImpersonationFetchInterceptor } from './lib/impersonation.js';
import { installDemoFetchGuard, startGuestDemo } from './lib/demo-customer.js';
import { registerServiceWorker, captureInstallPrompt } from './lib/pwa.js';
import { initSentry } from './lib/sentry.js';
import { initTelemetryStream } from './lib/telemetry-stream.js';
import { getSession } from './lib/auth.js';
import { seedDemoData } from './lib/demo-seed.js';
import './styles/index.css';

// Shareable demo link: ?demo=<accountNumber> auto-starts a sealed guest demo
// (same hermetic mock session as the "View a live demo" button) with sample
// requests + admin coverage seeded, so a single link drops someone straight
// into a representative fleet. Done FIRST + synchronously so the session +
// seed data exist before React mounts.
let __isDemoLink = false;
try {
  const demoAcct = new URLSearchParams(window.location.search).get('demo');
  if (demoAcct) {
    __isDemoLink = true;
    try { localStorage.setItem('netcom_connect_tour_dismissed', '1'); } catch { /* ignore */ }
    startGuestDemo(demoAcct);
    seedDemoData(demoAcct);
  }
} catch { /* ignore */ }

// Sentry — initialize FIRST so any subsequent boot-time error is captured
initSentry();
// Impersonation: when a Netcom admin is "viewing as" a customer, every
// fetch gets X-Assume-Account so the backend serves that customer's data.
installImpersonationFetchInterceptor();
// Compliance guardrail: block accidental fetches to forbidden hosts (Term Sheet § 10.2)
installFetchGuardrail();
// Demo kill-switch: while a public/guest demo session is active, hard-block
// every real-backend call. Installed LAST so it evaluates FIRST on each fetch.
installDemoFetchGuard();
// Telemetry stream — pass token getter so the WebSocket can authenticate
initTelemetryStream({ getToken: () => getSession?.()?.accessToken || null });
// PWA — service worker + install prompt (skip the install banner on demo
// links so it doesn't overlay the walkthrough).
registerServiceWorker();
if (!__isDemoLink) captureInstallPrompt();

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <ErrorBoundary scope="root">
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>
);
